import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";

const emptyForm = {
  name: "",
  storeName: "",
  email: "",
  password: "",
  phone: "",
  city: "",
  state: "Tamil Nadu",
  businessType: "retail",
  canBuyWholesale: false,
  canSellRetail: true,
  canSellWholesale: false,
  specialties: "",
  serviceableCities: "Chennai",
  wholesaleRegions: "Chennai, Chengalpattu, Kanchipuram, Tiruvallur",
  minimumOrderValue: "0",
  gstNumber: "",
};

const generateTemporaryPassword = () => {
  const chunk = Math.random().toString(36).slice(2, 6);
  const number = Math.floor(100 + Math.random() * 900);
  return `Seller@${chunk}${number}`;
};

const AdminSellers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sellers, setSellers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState("");
  const [editForm, setEditForm] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [loginLink, setLoginLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [businessFilter, setBusinessFilter] = useState(searchParams.get("business") || "all");
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "all");
  const [approvalFilter, setApprovalFilter] = useState(searchParams.get("approval") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const summary = {
    total: sellers.length,
    approved: sellers.filter((seller) => seller.isApproved).length,
    retailEnabled: sellers.filter((seller) => seller.canSellRetail !== false).length,
    wholesaleEnabled: sellers.filter((seller) => Boolean(seller.canSellWholesale)).length,
    tradeBuyers: sellers.filter((seller) => Boolean(seller.canBuyWholesale)).length,
  };

  const stateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          sellers
            .map((seller) => String(seller.state || "").trim())
            .filter(Boolean)
        )
      ).sort(),
    [sellers]
  );

  const filteredSellers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = sellers.filter((seller) => {
      const matchesBusiness = businessFilter === "all" || (seller.businessType || "retail") === businessFilter;
      const matchesState = stateFilter === "all" || String(seller.state || "").trim() === stateFilter;
      const matchesApproval =
        approvalFilter === "all" ||
        (approvalFilter === "approved" ? seller.isApproved : !seller.isApproved);
      const haystack = [
        seller.storeName,
        seller.name,
        seller.email,
        seller.phone,
        seller.city,
        seller.state,
        ...(seller.serviceableCities || []),
        ...(seller.wholesaleRegions || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      return matchesBusiness && matchesState && matchesApproval && matchesSearch;
    });
    return [...rows].sort((a, b) => {
      if (sort === "store") return String(a.storeName || "").localeCompare(String(b.storeName || ""));
      if (sort === "city") return String(a.city || "").localeCompare(String(b.city || ""));
      if (sort === "state") return String(a.state || "").localeCompare(String(b.state || ""));
      if (sort === "approval") return Number(Boolean(b.isApproved)) - Number(Boolean(a.isApproved));
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [approvalFilter, businessFilter, search, sellers, sort, stateFilter]);

  const loadSellers = async () => {
    try {
      const response = await api.get("/api/admin/sellers");
      setSellers(response.data.sellers || []);
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to load sellers");
    }
  };

  useEffect(() => {
    loadSellers();
  }, []);

  useEffect(() => {
    setBusinessFilter(searchParams.get("business") || "all");
    setStateFilter(searchParams.get("state") || "all");
    setApprovalFilter(searchParams.get("approval") || "all");
    setSearch(searchParams.get("q") || "");
    setSort(searchParams.get("sort") || "newest");
  }, [searchParams]);

  const setUrlState = (patch) => {
    const next = {
      business: businessFilter,
      state: stateFilter,
      approval: approvalFilter,
      q: search,
      sort,
      ...patch,
    };
    const params = new URLSearchParams();
    if (next.business && next.business !== "all") params.set("business", next.business);
    if (next.state && next.state !== "all") params.set("state", next.state);
    if (next.approval && next.approval !== "all") params.set("approval", next.approval);
    if (next.q && next.q.trim()) params.set("q", next.q.trim());
    if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
    setSearchParams(params);
  };

  const createSeller = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    setLoginLink("");
    try {
      const response = await api.post("/api/admin/sellers", {
        ...form,
        email: form.email.trim().toLowerCase(),
        password: form.password.trim(),
      });
      setForm(emptyForm);
      setMessage("Seller created successfully. Share the login link and temporary password with the seller.");
      setLoginLink(response?.data?.loginLink || `${window.location.origin}/seller/login`);
      await loadSellers();
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to create seller");
    } finally {
      setLoading(false);
    }
  };

  const updateSellerAccess = async (seller, field, value) => {
    try {
      const response = await api.put(`/api/admin/sellers/${seller._id}`, {
        [field]: value,
      });
      setSellers((prev) =>
        prev.map((row) => (row._id === seller._id ? response.data.seller : row))
      );
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to update access");
    }
  };

  const toggleApproval = async (seller) => {
    try {
      const response = await api.put(`/api/admin/sellers/${seller._id}`, {
        isApproved: !seller.isApproved,
      });
      setSellers((prev) =>
        prev.map((row) => (row._id === seller._id ? response.data.seller : row))
      );
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to update seller");
    }
  };

  const startEdit = (seller) => {
    setEditId(seller._id);
    setEditForm({
      name: seller.name || "",
      storeName: seller.storeName || "",
      email: seller.email || "",
      password: "",
      phone: seller.phone || "",
      city: seller.city || "",
      state: seller.state || "Tamil Nadu",
      businessType: seller.businessType || "retail",
      canBuyWholesale: Boolean(seller.canBuyWholesale),
      canSellRetail: seller.canSellRetail !== false,
      canSellWholesale: Boolean(seller.canSellWholesale),
      specialties: Array.isArray(seller.specialties) ? seller.specialties.join(", ") : "",
      serviceableCities: Array.isArray(seller.serviceableCities) ? seller.serviceableCities.join(", ") : "",
      wholesaleRegions: Array.isArray(seller.wholesaleRegions) ? seller.wholesaleRegions.join(", ") : "",
      minimumOrderValue: String(seller.minimumOrderValue || 0),
      gstNumber: seller.gstNumber || "",
    });
  };

  const saveSellerDetails = async (event) => {
    event.preventDefault();
    try {
      const passwordChanged = Boolean(editForm.password.trim());
      const payload = {
        ...editForm,
        email: editForm.email.trim().toLowerCase(),
        password: editForm.password.trim(),
      };
      if (!payload.password) delete payload.password;

      const response = await api.put(`/api/admin/sellers/${editId}`, payload);
      setSellers((prev) =>
        prev.map((row) => (row._id === editId ? response.data.seller : row))
      );
      setMessage(
        passwordChanged
          ? "Seller details updated and new temporary password saved. Share the new password with the seller."
          : "Seller details updated. Password was not changed."
      );
      setEditId("");
    } catch (err) {
      setMessage(err?.response?.data?.error || "Failed to update seller details");
    }
  };

  return (
    <div style={{ padding: 24, display: "grid", gap: 24 }}>
      <section style={panel}>
        <h2 style={{ marginTop: 0 }}>Create Trade Account</h2>
        <p style={{ marginTop: -6, color: "#64748b" }}>
          Use this only when onboarding a new retailer, shopkeeper, wholesaler, or hybrid partner. Store name also works
          as the seller user id during seller login.
        </p>
        <form onSubmit={createSeller} style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
          {[
            ["name", "Owner name"],
            ["storeName", "Store name / seller user id"],
            ["email", "Seller login email"],
            ["phone", "Phone"],
            ["city", "City"],
            ["state", "State"],
            ["specialties", "Specialties"],
            ["serviceableCities", "Serviceable cities"],
            ["wholesaleRegions", "Wholesale regions"],
            ["minimumOrderValue", "Minimum order value"],
            ["gstNumber", "GST number"],
          ].map(([field, label]) => (
            <input
              key={field}
              type={field === "minimumOrderValue" ? "number" : "text"}
              placeholder={label}
              value={form[field]}
              onChange={(event) => setForm((prev) => ({ ...prev, [field]: event.target.value }))}
              required={["name", "storeName", "email"].includes(field)}
              style={input}
            />
          ))}
          <div style={passwordBox}>
            <input
              type="text"
              placeholder="Temporary seller password"
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              required
              autoComplete="new-password"
              style={input}
            />
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, password: generateTemporaryPassword() }))}
            >
              Generate Password
            </button>
            <small style={{ color: "#64748b" }}>
              This is the password the seller will use at /seller/login with their email.
            </small>
          </div>
          <select
            value={form.businessType}
            onChange={(event) => {
              const businessType = event.target.value;
              setForm((prev) => ({
                ...prev,
                businessType,
                canBuyWholesale: ["retail", "hybrid"].includes(businessType),
                canSellRetail: ["retail", "hybrid"].includes(businessType),
                canSellWholesale: ["wholesale", "hybrid"].includes(businessType),
              }));
            }}
            style={input}
          >
            <option value="retail">Retailer / Shopkeeper</option>
            <option value="wholesale">Wholesaler / Supplier</option>
            <option value="hybrid">Retailer + Wholesaler</option>
          </select>
          <label style={checkLabel}>
            <input
              type="checkbox"
              checked={form.canBuyWholesale}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, canBuyWholesale: event.target.checked }))
              }
            />
            Can buy wholesale stock
          </label>
          <label style={checkLabel}>
            <input
              type="checkbox"
              checked={form.canSellRetail}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, canSellRetail: event.target.checked }))
              }
            />
            Can sell to end customers
          </label>
          <label style={checkLabel}>
            <input
              type="checkbox"
              checked={form.canSellWholesale}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, canSellWholesale: event.target.checked }))
              }
            />
            Can supply wholesale
          </label>
          <button type="submit" disabled={loading} style={{ gridColumn: "1 / -1" }}>
            {loading ? "Creating..." : "Create Trade Account"}
          </button>
        </form>
        {message ? <p style={{ color: "#0f766e", marginBottom: 0 }}>{message}</p> : null}
        {loginLink ? (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: "#eff6ff", color: "#1d4ed8" }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Share this seller login link</div>
            <div style={{ wordBreak: "break-all" }}>{loginLink}</div>
          </div>
        ) : null}
      </section>

      <section style={panel}>
        <div style={summaryGrid}>
          {[
            ["Trade Accounts", summary.total, { business: "all", approval: "all" }],
            ["Approved", summary.approved, { approval: "approved" }],
            ["Retail Enabled", summary.retailEnabled, { business: "retail" }],
            ["Wholesale Suppliers", summary.wholesaleEnabled, { business: "wholesale" }],
            ["Wholesale Buyers", summary.tradeBuyers, { business: "retail" }],
          ].map(([label, value, target]) => (
            <button key={label} type="button" style={summaryCard} onClick={() => setUrlState(target)}>
              <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 900, marginTop: 8 }}>{value}</div>
              <div style={{ color: "#0284c7", fontSize: 12, marginTop: 6, fontWeight: 800 }}>Open list</div>
            </button>
          ))}
        </div>
      </section>

      <section style={panel}>
        <h2 style={{ marginTop: 0 }}>Seller Management</h2>
        <div style={toolbar}>
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setUrlState({ q: event.target.value });
            }}
            placeholder="Search store, owner, email, phone, city..."
            style={input}
          />
          <select value={businessFilter} onChange={(event) => setUrlState({ business: event.target.value })} style={input}>
            <option value="all">All Types</option>
            <option value="retail">Retail</option>
            <option value="wholesale">Wholesale</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <select value={stateFilter} onChange={(event) => setUrlState({ state: event.target.value })} style={input}>
            <option value="all">All States</option>
            {stateOptions.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          <select value={approvalFilter} onChange={(event) => setUrlState({ approval: event.target.value })} style={input}>
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={sort} onChange={(event) => setUrlState({ sort: event.target.value })} style={input}>
            <option value="newest">Newest first</option>
            <option value="store">Store A-Z</option>
            <option value="city">City A-Z</option>
            <option value="state">State A-Z</option>
            <option value="approval">Approved first</option>
          </select>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 850 }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                <th style={th}>Store</th>
                <th style={th}>Type</th>
                <th style={th}>Access</th>
                <th style={th}>Contact</th>
                <th style={th}>Location</th>
                <th style={th}>Coverage</th>
                <th style={th}>Status</th>
                <th style={th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredSellers.map((seller) => (
                <tr key={seller._id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={td}>
                    <strong>{seller.storeName}</strong>
                    <div style={{ color: "#64748b", fontSize: 13 }}>{seller.name}</div>
                  </td>
                  <td style={td}>
                    <div style={{ textTransform: "capitalize" }}>{seller.businessType || "retail"}</div>
                    <div style={{ color: "#64748b", fontSize: 13 }}>
                      MOQ Rs.{Number(seller.minimumOrderValue || 0).toFixed(0)}
                    </div>
                  </td>
                  <td style={td}>
                    <label style={miniCheck}>
                      <input
                        type="checkbox"
                        checked={Boolean(seller.canBuyWholesale)}
                        onChange={(event) =>
                          updateSellerAccess(seller, "canBuyWholesale", event.target.checked)
                        }
                      />
                      Buy wholesale
                    </label>
                    <label style={miniCheck}>
                      <input
                        type="checkbox"
                        checked={seller.canSellRetail !== false}
                        onChange={(event) =>
                          updateSellerAccess(seller, "canSellRetail", event.target.checked)
                        }
                      />
                      Sell retail
                    </label>
                    <label style={miniCheck}>
                      <input
                        type="checkbox"
                        checked={Boolean(seller.canSellWholesale)}
                        onChange={(event) =>
                          updateSellerAccess(seller, "canSellWholesale", event.target.checked)
                        }
                      />
                      Supply wholesale
                    </label>
                  </td>
                  <td style={td}>
                    <div>{seller.email}</div>
                    <div style={{ color: "#64748b", fontSize: 13 }}>{seller.phone || "No phone"}</div>
                  </td>
                  <td style={td}>{[seller.city, seller.state].filter(Boolean).join(", ") || "-"}</td>
                  <td style={td}>
                    <div>{(seller.serviceableCities || []).join(", ") || "-"}</div>
                    <div style={{ color: "#64748b", fontSize: 13 }}>
                      {(seller.wholesaleRegions || []).join(", ") || (seller.specialties || []).join(", ") || "-"}
                    </div>
                  </td>
                  <td style={td}>{seller.isApproved ? "Approved" : "Suspended"}</td>
                  <td style={td}>
                    <button type="button" onClick={() => toggleApproval(seller)}>
                      {seller.isApproved ? "Suspend" : "Approve"}
                    </button>
                    <button type="button" onClick={() => startEdit(seller)} style={{ marginLeft: 8 }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredSellers.length === 0 ? <p style={{ marginTop: 12 }}>No sellers found for this filter.</p> : null}
      </section>

      {editId ? (
        <section style={panel}>
          <h2 style={{ marginTop: 0 }}>Edit Seller Details</h2>
          <p style={{ marginTop: -6, color: "#64748b" }}>
            Existing passwords are encrypted and cannot be viewed. To change access, enter a new temporary password below.
          </p>
          <form onSubmit={saveSellerDetails} style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
            {[
              ["name", "Owner name"],
              ["storeName", "Store name / seller user id"],
              ["email", "Seller login email"],
              ["phone", "Phone"],
              ["city", "City"],
              ["state", "State"],
              ["specialties", "Specialties"],
              ["serviceableCities", "Serviceable cities"],
              ["wholesaleRegions", "Wholesale regions"],
              ["minimumOrderValue", "Minimum order value"],
              ["gstNumber", "GST number"],
            ].map(([field, label]) => (
              <input
                key={field}
                type={field === "minimumOrderValue" ? "number" : "text"}
                placeholder={label}
                value={editForm[field]}
                onChange={(event) => setEditForm((prev) => ({ ...prev, [field]: event.target.value }))}
                style={input}
              />
            ))}
            <div style={passwordBox}>
              <input
                type="text"
                placeholder="New temporary password (optional)"
                value={editForm.password}
                onChange={(event) => setEditForm((prev) => ({ ...prev, password: event.target.value }))}
                autoComplete="new-password"
                style={input}
              />
              <button
                type="button"
                onClick={() => setEditForm((prev) => ({ ...prev, password: generateTemporaryPassword() }))}
              >
                Generate New Password
              </button>
              <small style={{ color: "#64748b" }}>
                Leave blank to keep the current seller password unchanged.
              </small>
            </div>
            <select
              value={editForm.businessType}
              onChange={(event) => setEditForm((prev) => ({ ...prev, businessType: event.target.value }))}
              style={input}
            >
              <option value="retail">Retailer / Shopkeeper</option>
              <option value="wholesale">Wholesaler / Supplier</option>
              <option value="hybrid">Retailer + Wholesaler</option>
            </select>
            <label style={checkLabel}>
              <input
                type="checkbox"
                checked={editForm.canBuyWholesale}
                onChange={(event) => setEditForm((prev) => ({ ...prev, canBuyWholesale: event.target.checked }))}
              />
              Can buy wholesale stock
            </label>
            <label style={checkLabel}>
              <input
                type="checkbox"
                checked={editForm.canSellRetail}
                onChange={(event) => setEditForm((prev) => ({ ...prev, canSellRetail: event.target.checked }))}
              />
              Can sell to end customers
            </label>
            <label style={checkLabel}>
              <input
                type="checkbox"
                checked={editForm.canSellWholesale}
                onChange={(event) => setEditForm((prev) => ({ ...prev, canSellWholesale: event.target.checked }))}
              />
              Can supply wholesale
            </label>
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
              <button type="submit">Save Seller Changes</button>
              <button type="button" onClick={() => setEditId("")}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      ) : null}
    </div>
  );
};

const panel = {
  background: "#fff",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
  border: "1px solid #e2e8f0",
};

const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #cbd5e1",
};

const checkLabel = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
};

const passwordBox = {
  display: "grid",
  gap: 8,
  padding: 12,
  border: "1px solid #bae6fd",
  borderRadius: 14,
  background: "#f0f9ff",
};

const miniCheck = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 6,
  fontSize: 13,
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};
const summaryCard = {
  textAlign: "left",
  cursor: "pointer",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 14,
};
const toolbar = {
  display: "grid",
  gridTemplateColumns: "minmax(240px, 1fr) repeat(4, minmax(150px, 210px))",
  gap: 12,
  marginBottom: 16,
};

const th = { padding: "12px 14px" };
const td = { padding: "12px 14px", verticalAlign: "top" };

export default AdminSellers;
